<template>
    <popup-modal position="center" @close="$emit('close')">
        <div class="w-904 p-16" ref="modalContainer">
            <div class="grid grid-cols-5 gap-x-40">
                <div class="col-span-full flex justify-between mb-16">
                    <h2 class="font-semibold text-heading-1">
                        {{ $t(`share_job.integrations.${card.type_ref}.popup.${activity}.title`) }}
                    </h2>
                    <div class="flex items-center gap-16">
                        <simple-button
                            v-if="card.type_ref === 'vdab' && !creatingVdabJob && vdabJob.active"
                            display="danger"
                            look="primary"
                            small
                            @click="$refs.vdabJobEditor.closeVdabJob()"
                        >
                            {{ $t('vdab.close_publication') }}
                        </simple-button>
                        <simple-button
                            v-if="card.type_ref === 'vdab' && !creatingVdabJob"
                            display="secondary"
                            look="primary"
                            small
                            @click="$refs.vdabJobEditor.saveVdabJob()"
                        >
                            {{ $t('general.publish') }}
                        </simple-button>
                    </div>
                </div>
                <div class="col-span-3">
                    <p
                        v-html="$t(`share_job.integrations.${card.type_ref}.popup.${activity}.body`, {'help_page': `<a href='${$t('helpdesk.vdab_integration')}' class='text-[#086D8E]'>${$t('general.help_page')}</a>`})"
                        class="text-body-sm"
                    ></p>
                    <div v-if="!subscription && card.type_ref !== 'multi-posting'">
                        <p class="text-14 font-medium italic mt-16">{{ $t('general.prices_excl_vat', { vat_label: vatLabelForRegion }) }}</p>
                        <checkbox  v-model="confirmed" class="flex justify-left mt-12 mb-16">
                            {{ $t('general.payment_confirmation') }}
                        </checkbox>
                    </div>
                    <div class="flex items-center gap-8 mt-32">
                        <simple-button
                            v-if="!subscription && card.type_ref === 'multi-posting'"
                            look="text"
                            :loading="performingContact"
                            @click="requestContactIntegration"
                        >
                            {{ $t('multi-posting.integrations.contact')}}
                        </simple-button>
                        <simple-button
                            v-if="!subscription"
                            class="w-full text-18"
                            display="secondary"
                            look="primary"
                            :uppercase="false"
                            :disabled="!confirmed && card.type_ref !== 'multi-posting'"
                            :loading="performingRequest"
                            @click="requestIntegration"
                        >
                            {{ $t(`share_job.integrations.${card.type_ref}.popup.${activity}.button`) }}
                        </simple-button>
                    </div>
                    <banner
                        v-if="vdabJob && vdabJob.vdab_url"
                        class="mt-16 !mb-0"
                        :link="normalizeUrl(vdabJob.vdab_url)"
                        :link-text="$t('vdab.to_vdab')"
                        :title="isExpiring ? $tc('vdab.unpublish', daysUntilUnpublished, {days: daysUntilUnpublished}) : $t('vdab.published')"
                        :description="$t('general.published_at', {
                            date: this.publishedAt.format('DD/MM/YYYY'),
                            time: this.publishedAt.format('HH:mm'),
                        })"
                        inline
                        :one-line="false"
                        :type="isExpiring ? 'warning' : 'info'"
                    />
                </div>
                <div class="col-span-2 rounded" :style="{ backgroundColor: '#F5F5F8' }">
                    <div
                        class="w-full h-full bg-no-repeat bg-center"
                        :style="{ backgroundImage: 'url(' + card.image_url + ')', backgroundSize: '100%' }"
                    ></div>
                </div>
            </div>
            <div v-if="subscription">
                <!-- section line -->
                <div class="border-t my-40"></div>

                <vdab-job-editor
                    :job="job"
                    :vdabJob="job.vdabJob"
                    :subscription="subscription"
                    ref="vdabJobEditor"
                    :scroll-to-top="scrollToTop"
                />
            </div>
        </div>
    </popup-modal>
</template>
<script>
import PopupModal from './ui/PopupModal.vue';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useNotificationsStore} from '../state/modules/notifications';
import Checkbox from './forms/Checkbox.vue';
import _upperFirst from 'lodash/upperFirst';
import SimpleButton from "./ui/SimpleButton.vue";
import ShareJobCard from "../models/ShareJobCard";
import PaidSubscription from "../models/PaidSubscription";
import * as vdab from "../services/VdabService";
import Company from "../models/Company";
import {normalizeJson, normalizeUrl} from "../../shared/helpers";
import {ADD_NOTIFICATION} from "../state/mutation-types";
import VdabJobEditor from "./ui/VdabJobEditor.vue";
import Job from "../models/Job";
import * as multiPosting from "../services/MultiPostingService";
import RegionLabel from '../mixins/regionLabel';
import Banner from "./ui/Banner.vue";
import moment from 'moment';

export default {
    components: {
        Banner,
        VdabJobEditor,
        SimpleButton,
        PopupModal,
        Checkbox,

    },
    props: {
        card: ShareJobCard,
        job: Job,
    },
    mixins: [RegionLabel],
    data() {
        return {
            confirmed: false,
            performingRequest: false,
            performingContact: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'currentUser', 'selectedLocale']),
        subscription() {
            return useRepo(PaidSubscription)
                .where('company_id', this.currentCompany.id)
                .where('type', this.card.subscription_type)
                .where('active', true)
                .first();
        },
        activity() {
            return this.subscription ? 'active' : 'inactive';
        },
        confirmationButton() {
            if (this.swapButtonLogic) {
                this.confirmButton = 'bg-white hover:bg-gray-50 text-gray-600 shadow';
            }

            if (!this.canConfirm) {
                return 'bg-gray-200 text-gray-600';
            }

            return (this.confirmButton === 'success' ? 'bg-green-light hover:bg-green' : this.confirmButton) || 'bg-red-600 hover:bg-red-500';
        },
        confirmationText() {
            return _upperFirst(this.confirmText || this.$t('general.confirm'));
        },
        vdabJob() {
            return this.job.vdabJob;
        },
        daysUntilUnpublished() {
            if (!this.vdabJob.active || !this.vdabJob.end_date) {
                return null;
            }

            return moment(this.vdabJob.end_date).diff(moment(), 'days') + 2;
        },
        isExpiring() {
            return this.daysUntilUnpublished !== null && parseInt(this.daysUntilUnpublished) <= 7;
        },
        publishedAt() {
            return this.vdabJob.published_at ?
                moment(this.vdabJob.published_at) : null;
        },
        creatingVdabJob() {
            return !(this.vdabJob !== null && this.vdabJob.vdab_id !== null);
        }
    },
    methods: {
        normalizeUrl,
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        requestContactIntegration() {
            this.performingContact = true;

            multiPosting.requestContact()
                .then(result => {
                    return useRepo(Company).save(normalizeJson(result));
                })
                .then(() => {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('messages.multi_posting.contact.success'),
                    });
                    this.$emit('close');
                })
                .finally(() => {
                    this.performingContact = false;
                });
        },
        requestIntegration() {
            if (this.card.type_ref === 'vdab') {
                this.vdabRequestIntegration();
            } else if (this.card.type_ref === 'multi-posting') {
                this.multiPostingIntegration();
            }
        },
        vdabRequestIntegration() {
            this.performingRequest = true;

            vdab.requestIntegration(this.currentUser.id)
                .then(result => {
                    return useRepo(Company).save(normalizeJson(result))
                }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('vdab.integration.success'),
                });
                this.$emit('close');
            }).finally(() => this.performingRequest = false);
        },
        multiPostingIntegration() {
            this.performingRequest = true;

            multiPosting.requestQuote()
                .then(result => {
                    useRepo(Company).save(normalizeJson(result))
                })
                .then(data => {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('messages.multi_posting.quote.success'),
                    });
                    this.$emit('close');
                })
                .finally(() => {
                    this.performingRequest = false
                });
        },
        scrollToTop() {
            this.$nextTick(() => {
                this.$refs.modalContainer.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            });
        },
    },
}
</script>
<style></style>
